import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useMe } from "@/api/hooks/useAuth";
import { loginAdresa } from "@/lib/prihlasenie";

/**
 * Auth gate — obaľuje chránené routes (AppShell).
 *
 * - Kým sa `/api/auth/me` načítava → fullscreen spinner (bránime flash-u
 *   obsahu aj zbytočnému redirectu).
 * - Auth zapnutá + nikto neprihlásený → redirect na /login s `?next=`
 *   (celá adresa **aj s query** — odkaz `/zakazky?koncept=…` z Tandem
 *   Zostáv musí po prihlásení otvoriť ten koncept, KOLO 127) a `from`
 *   v state ako doteraz.
 * - Auth vypnutá alebo user prihlásený → render children (pri vypnutej
 *   auth ostáva pôvodné správanie s UserNameDialog / X-Autor).
 */
export function RequireAuth({ children }: { children: ReactNode }): JSX.Element {
  const { data, isPending } = useMe();
  const location = useLocation();

  if (isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2
          className="h-8 w-8 animate-spin text-muted-foreground"
          aria-label="Načítavam…"
        />
      </div>
    );
  }

  const me = data?.data;
  if (me?.auth_enabled && !me.user) {
    return (
      <Navigate
        to={loginAdresa(`${location.pathname}${location.search}${location.hash}`)}
        state={{ from: location }}
        replace
      />
    );
  }

  return <>{children}</>;
}
